import {
  AttributeAssessmentId,
  ComplexityScopedAssessment,
  ComplexityAssessment,
  ComplexityAssessmentId,
  ComplexityScopedAssessmentMin,
  ComplexityModelId,
  ComplexityModelVersionId,
  ComplexityTierId,
  EngagementId,
  FilterComplexityAssessments,
  IValue,
  OwnerId,
  PendingBackgroundTask,
  RollForwardCandidate,
  RollForwardPlan,
  RollForwardReport,
  RollForwardRequest,
  ThirdPartyId,
  TprmScopeId,
} from "platformed-client/types";
import {platformedClient} from "../..";
import {invalidateQueries, q} from "../../../state";

export async function search(filter: FilterComplexityAssessments): Promise<ComplexityScopedAssessmentMin[]> {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/complexity_assessments/search", {
    body: filter,
  });
}

/** Search Scoped complexity-assessment batches. Mirrors `search` but returns one entry per Scoped batch. */
export async function searchBatches(filter: FilterComplexityAssessments): Promise<ComplexityAssessment[]> {
  return await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment_batches/search",
    {body: filter},
  );
}

export async function getById(assessmentId: ComplexityAssessmentId): Promise<ComplexityScopedAssessment> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}",
    {path: {complexity_assessment_id: assessmentId}},
  );
}

/** List the per-scope members of a complexity-assessment batch. */
export async function listScopes(assessmentId: ComplexityAssessmentId): Promise<ComplexityScopedAssessmentMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes",
    {path: {complexity_assessment_id: assessmentId}},
  );
}

/** Fetch the full complexity assessment for one batch member. */
export async function getMember(
  assessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
): Promise<ComplexityScopedAssessment> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}",
    {path: {complexity_assessment_id: assessmentId, scope_id: scopeId}},
  );
}

export async function create(
  thirdPartyId: ThirdPartyId,
  engagementId: EngagementId,
  complexityModelId: ComplexityModelId,
  complexityModelVersionId: ComplexityModelVersionId,
): Promise<ComplexityAssessmentId> {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/complexity_assessments", {
    body: {
      engagement_id: engagementId,
      complexity_model_id: complexityModelId,
      complexity_model_version_id: complexityModelVersionId,
    },
  });
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(thirdPartyId),
    q.tprm.engagementComplexityAssessments.filter(engagementId),
    q.tprm.engagementComplexityAssessmentBatches.filter(engagementId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function updateOwner(complexityAssessmentId: ComplexityAssessmentId, owner: OwnerId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/owner",
    {path: {complexity_assessment_id: complexityAssessmentId}, body: owner},
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
  ]);
  return res;
}

export async function reassessComplexity(complexityAssessmentId: ComplexityAssessmentId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/reassess",
    {path: {complexity_assessment_id: complexityAssessmentId}},
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  // Backend fans out one task per scope; we surface the first to
  // `useBackgroundTaskState` as a representative for the toast. Authoritative
  // "still running" state is driven by `has_running_attribute_tasks` on the
  // assessment, which sees all scopes.
  return res[0];
}

export async function updateComplexityTier(
  complexityAssessmentId: ComplexityAssessmentId,
  complexityTierId: ComplexityTierId,
  justification?: string,
) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/tier",
    {
      path: {complexity_assessment_id: complexityAssessmentId},
      body: {complexity_tier_id: complexityTierId, justification: justification ?? undefined},
    },
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function resetComplexityTier(complexityAssessmentId: ComplexityAssessmentId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/tier/reset",
    {path: {complexity_assessment_id: complexityAssessmentId}},
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function triggerAutoruns(engagementId: EngagementId, complexityAssessmentId: ComplexityAssessmentId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/trigger_autoruns",
    {path: {complexity_assessment_id: complexityAssessmentId}},
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.engagementAssessments.filter(engagementId),
    q.tprm.engagementAuditPlan.filter(engagementId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

/**
 * `clear` empties the attribute; `reset_to_ai` hands it back to the AI's
 * suggestion. They are different operations and cannot be collapsed into
 * "send a null value".
 */
export type AttributeValueUpdate =
  | {action: "set"; value: IValue}
  | {action: "clear"}
  | {action: "reset_to_ai"}
  | {action: "reset_to_rolled_forward"}
  | {action: "accept_rolled_forward"};

export async function updateAttributeValue(
  complexityAssessmentId: ComplexityAssessmentId,
  attributeAssessmentId: AttributeAssessmentId,
  update: AttributeValueUpdate,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/attribute/{attribute_assessment_id}",
    {
      path: {complexity_assessment_id: complexityAssessmentId, attribute_assessment_id: attributeAssessmentId},
      body: update,
    },
  );
  // An attribute change can flip the AI tier, which list mins surface.
  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function getRunningTasks(
  complexityAssessmentId: ComplexityAssessmentId,
): Promise<PendingBackgroundTask[]> {
  return (await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/running_tasks" as any,
    {path: {complexity_assessment_id: complexityAssessmentId}},
  )) as any;
}

export async function getLinkedRunningTasks(
  complexityAssessmentId: ComplexityAssessmentId,
): Promise<PendingBackgroundTask[]> {
  return (await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/linked_running_tasks" as any,
    {path: {complexity_assessment_id: complexityAssessmentId}},
  )) as any;
}

export async function reassessScopedComplexity(complexityAssessmentId: ComplexityAssessmentId, scopeId: TprmScopeId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/reassess",
    {path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId}},
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function updateScopedComplexityTier(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
  complexityTierId: ComplexityTierId,
  justification?: string,
) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/tier",
    {
      path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId},
      body: {complexity_tier_id: complexityTierId, justification: justification ?? undefined},
    },
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function resetScopedComplexityTier(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/tier/reset",
    {path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId}},
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function updateScopedAttributeValue(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
  attributeAssessmentId: AttributeAssessmentId,
  update: AttributeValueUpdate,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/attribute/{attribute_assessment_id}",
    {
      path: {
        complexity_assessment_id: complexityAssessmentId,
        scope_id: scopeId,
        attribute_assessment_id: attributeAssessmentId,
      },
      body: update,
    },
  );
  // An attribute change can flip the AI tier, which the rollup widget surfaces.
  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
  return res;
}

export async function delete_(complexityAssessmentId: ComplexityAssessmentId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}" as any,
    {path: {complexity_assessment_id: complexityAssessmentId}},
  );
  await invalidateQueries([
    q.tprm.thirdPartyComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessments.filter(),
    q.tprm.engagementComplexityAssessmentBatches.filter(),
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.usedComplexityTiersOnAccount.filter(),
  ]);
}

export async function listRollForwardCandidates(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
): Promise<RollForwardCandidate[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/roll_forward/candidates",
    {path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId}},
  );
}

export async function planRollForward(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
  request: RollForwardRequest,
): Promise<RollForwardPlan> {
  return await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/roll_forward/plan",
    {path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId}, body: request},
  );
}

export async function getRollForward(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
): Promise<RollForwardReport | null> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/roll_forward",
    {path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId}},
  );
}

export async function rollForward(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
  request: RollForwardRequest,
): Promise<PendingBackgroundTask> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/roll_forward",
    {path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId}, body: request},
  );
  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.complexityRollForward.filter(complexityAssessmentId),
    q.tprm.complexityRollForwardPlan.filter(complexityAssessmentId),
  ]);
  return res;
}

export async function undoRollForward(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
): Promise<RollForwardReport> {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/roll_forward",
    {path: {complexity_assessment_id: complexityAssessmentId, scope_id: scopeId}},
  );
  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentScopes.filter(complexityAssessmentId),
    q.tprm.complexityRollForward.filter(complexityAssessmentId),
    q.tprm.complexityRollForwardPlan.filter(complexityAssessmentId),
  ]);
  return res;
}

async function invalidateAttribute(complexityAssessmentId: ComplexityAssessmentId) {
  await invalidateQueries([
    q.tprm.complexityAssessment.filter(complexityAssessmentId),
    q.tprm.complexityAssessmentMember.filter(complexityAssessmentId),
  ]);
}

export async function confirmAttribute(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
  attributeAssessmentId: AttributeAssessmentId,
): Promise<void> {
  await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/attribute/{attribute_assessment_id}/confirmation",
    {
      path: {
        complexity_assessment_id: complexityAssessmentId,
        scope_id: scopeId,
        attribute_assessment_id: attributeAssessmentId,
      },
    },
  );
  await invalidateAttribute(complexityAssessmentId);
}

export async function unconfirmAttribute(
  complexityAssessmentId: ComplexityAssessmentId,
  scopeId: TprmScopeId,
  attributeAssessmentId: AttributeAssessmentId,
): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/complexity_assessment/{complexity_assessment_id}/scopes/{scope_id}/attribute/{attribute_assessment_id}/confirmation",
    {
      path: {
        complexity_assessment_id: complexityAssessmentId,
        scope_id: scopeId,
        attribute_assessment_id: attributeAssessmentId,
      },
    },
  );
  await invalidateAttribute(complexityAssessmentId);
}
